import React, { useEffect, useState } from "react";
import InlineSpinner from "../components/ui/InlineSpinner";
import { Eye, LogOut, RefreshCw, BarChart2, Calendar, Users, TrendingUp, Download, ChevronDown, ChevronUp } from "lucide-react";
import lugarDashboardService from "../services/lugarDashboardService";
import { useAuth } from "../hooks/useAuth";
import DetalleEvento from "../components/Modales/DetalleEvento";
import { Tooltip } from "../components/ui/Tooltip";

/**
 * Home para usuarios con rol 'lugar'
/**
 * Helpers para cálculo de boletos pagados en base a abonos
 */
export const getCostoBoleto = (ev) => {
  const costo = Number(ev.evento?.costo || ev.costo || ev.evento?.precio || 0);
  if (costo > 0) return costo;
  if (ev.boletosApartados > 0 && ev.boletosApartadosDinero > 0) {
    return ev.boletosApartadosDinero / ev.boletosApartados;
  }
  return 0;
};

export const calcularBoletosPagados = (ev) => {
  const costo = getCostoBoleto(ev);
  const abono = Number(ev.abonoRealizado || 0);
  const apartados = Number(ev.boletosApartados || 0);

  const boletosPagados = costo > 0 ? Math.floor(abono / costo) : 0;
  const porcentajePagados =
    apartados > 0 ? Math.round((boletosPagados / apartados) * 100) : 0;

  return { boletosPagados, porcentajePagados };
};

export default function HomeLugar() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const { logout, user } = useAuth();
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [eventoSeleccionado, setEventoSeleccionado] = useState(null);
  const [modalDetalleOpen, setModalDetalleOpen] = useState(false);

  const formatDate = (iso) => {
    if (!iso) return "-";
    try {
      const d = new Date(iso);
      return (
        d.toLocaleDateString(undefined, {
          day: "2-digit",
          month: "short",
          year: "numeric",
        }) +
        " " +
        d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })
      );
    } catch {
      return iso;
    }
  };

  const formatDateShort = (iso) => {
    if (!iso) return "-";
    try {
      const d = new Date(iso);
      return d.toLocaleDateString(undefined, {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch {
      return iso;
    }
  };

  const loadData = async (lugarId, { refreshing = false } = {}) => {
    if (refreshing) setIsRefreshing(true);
    else setLoading(true);
    try {
      const res = await lugarDashboardService.getResumen(lugarId);
      console.log("Resumen cargado:", res);
      setData(res);
      setLastUpdated(new Date());
      setError(null);
    } catch (e) {
      setError("No se pudo cargar el resumen");
    } finally {
      if (refreshing) setIsRefreshing(false);
      else setLoading(false);
    }
  };

  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!user) return; // esperar usuario disponible
      const lugarId = user?.lugarId || user?.lugar_id || null;
      if (!mounted) return;
      await loadData(lugarId);
    })();
    return () => {
      mounted = false;
    };
  }, [user]);

  const handleRefresh = async () => {
    if (isRefreshing || !user) return;
    const lugarId = user?.lugarId || user?.lugar_id || null;
    await loadData(lugarId, { refreshing: true });
  };

  const handleLogout = async () => {
    if (isLoggingOut) return;
    setIsLoggingOut(true);
    try {
      await logout();
    } catch (e) {
      setIsLoggingOut(false);
    }
  };

  const handleExportToExcel = () => {
    if (!data) return;

    try {
      // Importar xlsx de forma dinámica
      import("xlsx")
        .then((XLSX) => {
          const workbook = XLSX.utils.book_new();

          // Formato de fecha de última actualización
          const fechaActualizacion = lastUpdated
            ? formatDate(lastUpdated.toISOString())
            : "No disponible";

          // Hoja 1: Resumen General (KPIs)
          const resumenData = [
            ["RESUMEN GENERAL - " + lugarNombre],
            ["Última actualización: " + fechaActualizacion],
            [""],
            ["Métrica", "Valor"],
            ["Eventos totales", totales.eventos],
            ["Alumnos", totales.asistentesAlumnos],
            ["Ocupación promedio", totales.ocupacionPromedio + "%"],
            [""],
            ["BOLETOS Y PAGOS"],
            [
              "Boletos apartados",
              totales.boletosApartados,
              "$" + totales.boletosApartadosDinero.toLocaleString(),
            ],
            [
              "Boletos pagados",
              totales.boletosPagados,
              "$" +
                totales.boletosPagadosDinero.toLocaleString() +
                " (" +
                totales.porcentajePagados +
                "%)",
            ],
            [
              "Abono realizado",
              "",
              "$" +
                totales.abonoRealizado.toLocaleString() +
                " (" +
                totales.porcentajeAbonado +
                "%)",
            ],
            [
              "Boletos por pagar",
              totales.boletosPorPagar,
              "$" +
                totales.boletosPorPagarDinero.toLocaleString() +
                " (" +
                (100 - totales.porcentajePagados) +
                "%)",
            ],
            [
              "Tasa de pago",
              totales.porcentajePagados + "%",
              totales.boletosPagados +
                " de " +
                totales.boletosApartados +
                " pagados",
            ],
          ];
          const wsResumen = XLSX.utils.aoa_to_sheet(resumenData);
          XLSX.utils.book_append_sheet(workbook, wsResumen, "Resumen");

          // Hoja 2: Próximos Eventos
          if (proximosEventos && proximosEventos.length > 0) {
            const proximosData = [
              ["PRÓXIMOS EVENTOS"],
              ["Última actualización: " + fechaActualizacion],
              [""],
              [
                "Fecha",
                "Nombre",
                "Tipo",
                "Alumnos",
                "Apartados",
                "Apartados $",
                "Pagados",
                "Pagados $",
                "Abonado $",
                "Por Pagar",
                "Por Pagar $",
                "% Pagado",
                "% Abonado",
              ],
            ];

            proximosEventos.forEach((ev) => {
              const calc = calcularBoletosPagados(ev);
              proximosData.push([
                formatDate(ev.evento?.fecha_evento),
                ev.evento?.nombre_evento || "-",
                ev.evento?.tipo || "-",
                ev.asistentesAlumnos || "-",
                ev.boletosApartados,
                ev.boletosApartadosDinero,
                // ev.boletosPagados,
                calc.boletosPagados,
                ev.boletosPagadosDinero,
                ev.abonoRealizado,
                ev.boletosPorPagar,
                ev.boletosPorPagarDinero,
                // ev.porcentajePagados + "%",
                calc.porcentajePagados + "%",
                ev.porcentajeAbonado + "%",
              ]);
            });

            const wsProximos = XLSX.utils.aoa_to_sheet(proximosData);
            XLSX.utils.book_append_sheet(
              workbook,
              wsProximos,
              "Próximos Eventos"
            );
          }

          // Hoja 3: Eventos Recientes
          if (eventosRecientes && eventosRecientes.length > 0) {
            const recientesData = [
              ["EVENTOS RECIENTES"],
              ["Última actualización: " + fechaActualizacion],
              [""],
              [
                "Fecha",
                "Nombre",
                "Tipo",
                "Alumnos",
                "Con boletos",
                "Ocupación",
                "Apartados",
                "Apartados $",
                "Pagados",
                "Pagados $",
                "Abonado $",
                "Por Pagar",
                "Por Pagar $",
                "% Pagado",
                "% Abonado",
              ],
            ];

            eventosRecientes.forEach((ev) => {
              const calc = calcularBoletosPagados(ev);
              recientesData.push([
                formatDate(ev.evento?.fecha_evento),
                ev.evento?.nombre_evento || "-",
                ev.evento?.tipo || "-",
                ev.asistentesAlumnos || "-",
                ev.asistentes,
                ev.ocupacion + "%",
                ev.boletosApartados,
                ev.boletosApartadosDinero,
                // ev.boletosPagados,
                calc.boletosPagados,
                ev.boletosPagadosDinero,
                ev.abonoRealizado,
                ev.boletosPorPagar,
                ev.boletosPorPagarDinero,
                // ev.porcentajePagados + "%",
                calc.porcentajePagados + "%",
                ev.porcentajeAbonado + "%",
              ]);
            });

            const wsRecientes = XLSX.utils.aoa_to_sheet(recientesData);
            XLSX.utils.book_append_sheet(
              workbook,
              wsRecientes,
              "Eventos Recientes"
            );
          }

          // Generar archivo
          const fecha = new Date().toISOString().split("T")[0];
          const nombreArchivo = `${lugarNombre.replace(
            /[^a-z0-9]/gi,
            "_"
          )}_${fecha}.xlsx`;
          XLSX.writeFile(workbook, nombreArchivo);
        })
        .catch((err) => {
          console.error("Error al cargar xlsx:", err);
          alert("Error al exportar. Por favor, intenta nuevamente.");
        });
    } catch (error) {
      console.error("Error en exportación:", error);
      alert("Error al exportar a Excel");
    }
  };

  const handleVerDetalle = (evento) => {
    setEventoSeleccionado(evento);
    setModalDetalleOpen(true);
  };

  const handleCloseDetalle = () => {
    setModalDetalleOpen(false);
    setEventoSeleccionado(null);
  };

  if (loading) {
    return <HomeLugarSkeleton />;
  }

  if (error) {
    return <div className="p-8 text-red-600 dark:text-red-400">{error}</div>;
  }

  if (!data) {
    return <div className="p-8">Sin datos disponibles.</div>;
  }

  const {
    lugarNombre,
    totales,
    proximosEventos,
    eventosRecientes,
    topTiposEventos,
  } = data;

  return (
    <div className="min-h-screen bg-fondoVs">
      {/* ── Header ── */}
      <div className="bg-casalds-700">
        <header className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 sm:p-5 lg:p-6 max-w-7xl mx-auto">
          {/* Logo + Titulo */}
          <div className="flex items-center gap-3 min-w-0">
            <div className="shrink-0">
              <img
                src="logop.png"
                alt="Logo Casal"
                className="h-10 sm:h-12 lg:h-14"
              />
            </div>
            <div className="min-w-0">
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-semibold text-white leading-tight">
                Resumen de tu lugar
              </h1>
              <p className="text-sm text-gray-200 truncate">{lugarNombre}</p>
              <p className="text-xs text-gray-300">
                Última actualización:{" "}
                {lastUpdated ? formatDate(lastUpdated.toISOString()) : "—"}
              </p>
            </div>
          </div>

          {/* Acciones */}
          <div className="flex items-center gap-2 self-end sm:self-auto">
            <Tooltip content="Exportar a Excel" position="bottom">
              <button
                onClick={handleExportToExcel}
                disabled={!data || loading}
                className="px-3 py-2 bg-casal/80 hover:bg-[#155059] text-white rounded-lg flex items-center gap-2 shadow disabled:opacity-60 disabled:cursor-not-allowed transition text-sm"
              >
                <Download size={16} />
                <span className="hidden xs:inline sm:hidden lg:inline">Exportar</span>
              </button>
            </Tooltip>
            <Tooltip content="Actualizar" position="bottom">
              <button
                onClick={handleRefresh}
                disabled={isRefreshing || loading}
                aria-busy={isRefreshing}
                className="w-9 h-9 bg-white dark:bg-gray-200 rounded-full flex items-center justify-center shadow disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {isRefreshing ? (
                  <InlineSpinner size="xs" />
                ) : (
                  <RefreshCw size={16} className="text-gray-600 dark:text-gray-800" />
                )}
              </button>
            </Tooltip>
            <Tooltip content="Cerrar Sesión" position="bottom">
              <button
                onClick={handleLogout}
                disabled={isLoggingOut}
                aria-busy={isLoggingOut}
                className="w-9 h-9 bg-white dark:bg-gray-200 rounded-full flex items-center justify-center shadow disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {isLoggingOut ? (
                  <InlineSpinner size="xs" />
                ) : (
                  <LogOut size={16} className="text-gray-600 dark:text-gray-800" />
                )}
              </button>
            </Tooltip>
          </div>
        </header>
      </div>

      {/* ── Contenido principal ── */}
      <div className="p-4 sm:p-5 lg:p-8 max-w-7xl mx-auto space-y-5 sm:space-y-6 lg:space-y-8">

        {/* KPIs principales: 1 col mobile → 3 cols tablet → 3 cols desktop */}
        <section className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
          <KpiCard
            icon={<Calendar className="w-5 h-5 sm:w-6 sm:h-6" />}
            label="Eventos totales"
            value={totales.eventos}
          />
          <KpiCard
            icon={<Users className="w-5 h-5 sm:w-6 sm:h-6" />}
            label="Alumnos"
            value={totales.asistentesAlumnos}
          />
          <KpiCard
            icon={<TrendingUp className="w-5 h-5 sm:w-6 sm:h-6" />}
            label="Ocupación promedio"
            value={totales.ocupacionPromedio + "%"}
          />
        </section>

        {/* KPIs de Boletos: 2 cols mobile → 2-3 cols tablet → 5 cols desktop */}
        <section className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
          <KpiCard
            icon={<Users className="w-5 h-5" />}
            label="Boletos apartados"
            value={totales.boletosApartados.toLocaleString()}
            subtitle={"$" + totales.boletosApartadosDinero.toLocaleString()}
          />
          <KpiCard
            icon={<TrendingUp className="w-5 h-5" />}
            label="Boletos pagados"
            value={totales.boletosPagados.toLocaleString()}
            subtitle={
              "$" +
              totales.boletosPagadosDinero.toLocaleString() +
              " (" +
              totales.porcentajePagados +
              "%)"
            }
            highlight="success"
          />
          <KpiCard
            icon={<BarChart2 className="w-5 h-5" />}
            label="Abono realizado"
            value={"$" + totales.abonoRealizado.toLocaleString()}
            subtitle={totales.porcentajeAbonado + "% del total abonado"}
            highlight="info"
          />
          <KpiCard
            icon={<BarChart2 className="w-5 h-5" />}
            label="Boletos por pagar"
            value={totales.boletosPorPagar.toLocaleString()}
            subtitle={
              "$" +
              totales.boletosPorPagarDinero.toLocaleString() +
              " (" +
              (100 - totales.porcentajePagados) +
              "%)"
            }
            highlight="warning"
          />
          {/* En mobile esta card ocupa 2 columnas para centrarla */}
          <div className="col-span-2 sm:col-span-1">
            <KpiCard
              icon={<TrendingUp className="w-5 h-5" />}
              label="Tasa de pago"
              value={totales.porcentajePagados + "%"}
              subtitle={
                totales.boletosPagados +
                " de " +
                totales.boletosApartados +
                " pagados"
              }
            />
          </div>
        </section>

        {/* ── Próximos eventos ── */}
        <section className="space-y-3">
          <h2 className="text-base sm:text-lg lg:text-xl font-semibold text-gray-800 dark:text-gray-100">
            Próximos eventos
          </h2>

          {proximosEventos.length === 0 ? (
            <EmptyState mensaje="No hay eventos próximos" />
          ) : (
            <>
              {/* Vista móvil: tarjetas */}
              <div className="lg:hidden space-y-3">
                {proximosEventos.map((ev, idx) => (
                  <EventoCardProximo
                    key={ev.evento?.id || idx}
                    ev={ev}
                    formatDate={formatDate}
                    formatDateShort={formatDateShort}
                    onVerDetalle={handleVerDetalle}
                  />
                ))}
              </div>

              {/* Vista desktop: tabla */}
              <div className="hidden lg:block bg-white dark:bg-[#1e1e1e] rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                <table className="w-full table-fixed divide-y divide-gray-200 dark:divide-gray-700 text-sm">
                  <colgroup>
                    <col style={{width: '11%'}} />
                    <col style={{width: '18%'}} />
                    <col style={{width: '8%'}} />
                    <col style={{width: '6%'}} />
                    <col style={{width: '10%'}} />
                    <col style={{width: '10%'}} />
                    <col style={{width: '10%'}} />
                    <col style={{width: '10%'}} />
                    <col style={{width: '7%'}} />
                    <col style={{width: '7%'}} />
                    <col style={{width: '6%'}} />
                  </colgroup>
                  <thead className="bg-casal dark:bg-[#23272e]">
                    <tr>
                      <Th>Fecha</Th>
                      <Th>Nombre</Th>
                      <Th>Tipo</Th>
                      <Th>Alumnos</Th>
                      <Th>Apartados</Th>
                      <Th>Pagados</Th>
                      <Th>Abonado</Th>
                      <Th>Por Pagar</Th>
                      <Th>% Pagado</Th>
                      <Th>% Abonado</Th>
                      <Th>Acc.</Th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-[#1e1e1e] divide-y divide-gray-100 dark:divide-gray-800">
                    {proximosEventos.map((ev, idx) => (
                      <tr
                        key={ev.evento?.id || idx}
                        className="hover:bg-gray-50 dark:hover:bg-[#23272e] transition"
                      >
                        <TdWrap className="text-xs">{formatDate(ev.evento?.fecha_evento)}</TdWrap>
                        <TdWrap className="font-medium">{ev.evento?.nombre_evento}</TdWrap>
                        <TdWrap className="text-xs">{ev.evento?.tipo}</TdWrap>
                        <Td>{ev.asistentesAlumnos || "-"}</Td>
                        <Td>
                          <div>{ev.boletosApartados}</div>
                          <div className="text-xs text-gray-500">
                            ${ev.boletosApartadosDinero.toLocaleString()}
                          </div>
                        </Td>
                        {/* --- CÁLCULO ANTERIOR (Solo alumnos 100% liquidados) ---
                        <Td>
                          <div className="text-green-600 dark:text-green-400 font-medium">
                            {ev.boletosPagados}
                          </div>
                          <div className="text-xs text-gray-500">
                            ${ev.boletosPagadosDinero.toLocaleString()}
                          </div>
                        </Td>
                        */}
                        {/* --- NUEVO CÁLCULO (Abono Realizado / Costo boleto) --- */}
                        <Td>
                          <div className="text-green-600 dark:text-green-400 font-medium">
                            {calcularBoletosPagados(ev).boletosPagados}
                          </div>
                          {/* <div className="text-xs text-gray-500">
                            ${ev.boletosPagadosDinero.toLocaleString()}
                          </div> */}
                        </Td>
                        <Td>
                          <div className="text-blue-600 dark:text-blue-400 font-medium">
                            ${ev.abonoRealizado.toLocaleString()}
                          </div>
                          <div className="text-xs text-gray-500">
                            Abono adicional
                          </div>
                        </Td>
                        <Td>
                          <div className="text-amber-600 dark:text-amber-400 font-medium">
                            {ev.boletosPorPagar}
                          </div>
                          <div className="text-xs text-gray-500">
                            ${ev.boletosPorPagarDinero.toLocaleString()}
                          </div>
                        </Td>
                        {/* --- PORCENTAJE ANTERIOR ---
                        <Td>
                          <Badge
                            color={ev.porcentajePagados >= 50 ? "green" : "amber"}
                          >
                            {ev.porcentajePagados}%
                          </Badge>
                        </Td>
                        */}
                        {/* --- NUEVO PORCENTAJE (Basado en boletos cubiertos por abono) --- */}
                        <Td>
                          <Badge
                            color={
                              calcularBoletosPagados(ev).porcentajePagados >= 50
                                ? "green"
                                : "amber"
                            }
                          >
                            {calcularBoletosPagados(ev).porcentajePagados}%
                          </Badge>
                        </Td>
                        <Td>
                          <Badge
                            color={ev.porcentajeAbonado >= 20 ? "blue" : "gray"}
                          >
                            {ev.porcentajeAbonado}%
                          </Badge>
                        </Td>
                        <Td>
                          <Tooltip content="Ver detalles del evento" position="left">
                            <button
                              onClick={() => handleVerDetalle(ev)}
                              className="p-2 rounded-lg bg-[#246370] hover:bg-[#1d4f5a] dark:bg-[#2a9d8f] dark:hover:bg-[#238276] text-white transition-colors shadow-sm"
                            >
                              <Eye size={16} />
                            </button>
                          </Tooltip>
                        </Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </section>

        {/* ── Eventos recientes ── */}
        <section className="space-y-3">
          <h2 className="text-base sm:text-lg lg:text-xl font-semibold text-gray-800 dark:text-gray-100">
            Eventos recientes
          </h2>

          {eventosRecientes.length === 0 ? (
            <EmptyState mensaje="No hay eventos registrados" />
          ) : (
            <>
              {/* Vista móvil: tarjetas */}
              <div className="lg:hidden space-y-3">
                {eventosRecientes.map((ev, idx) => (
                  <EventoCardReciente
                    key={ev.evento?.id || idx}
                    ev={ev}
                    formatDate={formatDate}
                    formatDateShort={formatDateShort}
                    onVerDetalle={handleVerDetalle}
                  />
                ))}
              </div>

              {/* Vista desktop: tabla */}
              <div className="hidden lg:block bg-white dark:bg-[#1e1e1e] rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                <table className="w-full table-fixed divide-y divide-gray-200 dark:divide-gray-700 text-sm">
                  <colgroup>
                    <col style={{width: '9%'}} />
                    <col style={{width: '17%'}} />
                    <col style={{width: '7%'}} />
                    <col style={{width: '5%'}} />
                    <col style={{width: '6%'}} />
                    <col style={{width: '6%'}} />
                    <col style={{width: '9%'}} />
                    <col style={{width: '9%'}} />
                    <col style={{width: '9%'}} />
                    <col style={{width: '9%'}} />
                    <col style={{width: '7%'}} />
                    <col style={{width: '7%'}} />
                    <col style={{width: '5%'}} />
                  </colgroup>
                  <thead className="bg-casal dark:bg-[#23272e]">
                    <tr>
                      <Th>Fecha</Th>
                      <Th>Nombre</Th>
                      <Th>Tipo</Th>
                      <Th>Alumnos</Th>
                      <Th>Boletos</Th>
                      <Th>Ocup.</Th>
                      <Th>Apartados</Th>
                      <Th>Pagados</Th>
                      <Th>Abonado</Th>
                      <Th>Por Pagar</Th>
                      <Th>% Pag.</Th>
                      <Th>% Abon.</Th>
                      <Th>Acc.</Th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-[#1e1e1e] divide-y divide-gray-100 dark:divide-gray-800">
                    {eventosRecientes.map((ev, idx) => (
                      <tr
                        key={ev.evento?.id || idx}
                        className="hover:bg-gray-50 dark:hover:bg-[#23272e] transition"
                      >
                        <TdWrap className="text-xs font-medium">
                          {formatDateShort(ev.evento?.fecha_evento)}
                        </TdWrap>
                        <TdWrap className="font-medium">{ev.evento?.nombre_evento}</TdWrap>
                        <TdWrap className="text-xs">{ev.evento?.tipo}</TdWrap>
                        <Td>{ev.asistentesAlumnos || "-"}</Td>
                        <Td>{ev.asistentes}</Td>
                        <Td>{ev.ocupacion}%</Td>
                        <Td>
                          <div>{ev.boletosApartados}</div>
                          <div className="text-xs text-gray-500">
                            ${ev.boletosApartadosDinero.toLocaleString()}
                          </div>
                        </Td>
                        {/* --- CÁLCULO ANTERIOR (Solo alumnos 100% liquidados) ---
                        <Td>
                          <div className="text-green-600 dark:text-green-400 font-medium">
                            {ev.boletosPagados}
                          </div>
                          <div className="text-xs text-gray-500">
                            ${ev.boletosPagadosDinero.toLocaleString()}
                          </div>
                        </Td>
                        */}
                        {/* --- NUEVO CÁLCULO (Abono Realizado / Costo boleto) --- */}
                        <Td>
                          <div className="text-green-600 dark:text-green-400 font-medium">
                            {calcularBoletosPagados(ev).boletosPagados}
                          </div>
                          {/* <div className="text-xs text-gray-500">
                            ${ev.boletosPagadosDinero.toLocaleString()}
                          </div> */}
                        </Td>
                        <Td>
                          <div className="text-blue-600 dark:text-blue-400 font-medium">
                            ${ev.abonoRealizado.toLocaleString()}
                          </div>
                          <div className="text-xs text-gray-500">
                            Abono adicional
                          </div>
                        </Td>
                        <Td>
                          <div className="text-amber-600 dark:text-amber-400 font-medium">
                            {ev.boletosPorPagar}
                          </div>
                          <div className="text-xs text-gray-500">
                            ${ev.boletosPorPagarDinero.toLocaleString()}
                          </div>
                        </Td>
                        {/* --- PORCENTAJE ANTERIOR ---
                        <Td>
                          <Badge
                            color={ev.porcentajePagados >= 50 ? "green" : "amber"}
                          >
                            {ev.porcentajePagados}%
                          </Badge>
                        </Td>
                        */}
                        {/* --- NUEVO PORCENTAJE (Basado en boletos cubiertos por abono) --- */}
                        <Td>
                          <Badge
                            color={
                              calcularBoletosPagados(ev).porcentajePagados >= 50
                                ? "green"
                                : "amber"
                            }
                          >
                            {calcularBoletosPagados(ev).porcentajePagados}%
                          </Badge>
                        </Td>
                        <Td>
                          <Badge
                            color={ev.porcentajeAbonado >= 20 ? "blue" : "gray"}
                          >
                            {ev.porcentajeAbonado}%
                          </Badge>
                        </Td>
                        <Td>
                          <Tooltip content="Ver detalles del evento" position="left">
                            <button
                              onClick={() => handleVerDetalle(ev)}
                              className="p-2 rounded-lg bg-[#246370] hover:bg-[#1d4f5a] dark:bg-[#2a9d8f] dark:hover:bg-[#238276] text-white transition-colors shadow-sm"
                            >
                              <Eye size={16} />
                            </button>
                          </Tooltip>
                        </Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </section>
      </div>

      {/* Modal de detalle de evento */}
      <DetalleEvento
        open={modalDetalleOpen}
        onClose={handleCloseDetalle}
        evento={eventoSeleccionado}
      />
    </div>
  );
}

/* ──────────────────────────────────────────────
   Tarjeta de evento próximo (móvil/tablet)
────────────────────────────────────────────── */
function EventoCardProximo({ ev, formatDate, formatDateShort, onVerDetalle }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="bg-white dark:bg-[#1e1e1e] rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden shadow-sm">
      {/* Cabecera de la tarjeta */}
      <div className="flex items-start justify-between gap-3 p-4">
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-gray-800 dark:text-gray-100 truncate text-sm">
            {ev.evento?.nombre_evento || "-"}
          </p>
          <p className="text-xs text-gray-500 mt-0.5">
            {formatDateShort(ev.evento?.fecha_evento)}
            {ev.evento?.tipo ? ` · ${ev.evento.tipo}` : ""}
            {ev.asistentesAlumnos ? ` · ${ev.asistentesAlumnos} alumnos` : ""}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={() => onVerDetalle(ev)}
            className="p-2 rounded-lg bg-[#246370] hover:bg-[#1d4f5a] text-white transition-colors shadow-sm"
          >
            <Eye size={15} />
          </button>
          <button
            onClick={() => setExpanded((v) => !v)}
            className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-600 dark:text-gray-300 transition-colors"
            aria-label={expanded ? "Ocultar detalles" : "Ver detalles"}
          >
            {expanded ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
          </button>
        </div>
      </div>

      {/* Resumen siempre visible */}
      <div className="grid grid-cols-3 divide-x divide-gray-100 dark:divide-gray-700 border-t border-gray-100 dark:border-gray-700 px-1">
        <MiniStat label="Apartados" value={ev.boletosApartados} />
        {/* --- CÁLCULO ANTERIOR ---
        <MiniStat
          label="Pagados"
          value={ev.boletosPagados}
          colorClass="text-green-600 dark:text-green-400"
        />
        <MiniStat
          label="% Pagado"
          value={`${ev.porcentajePagados}%`}
          colorClass={ev.porcentajePagados >= 50 ? "text-green-600 dark:text-green-400" : "text-amber-600 dark:text-amber-400"}
        />
        */}
        {/* --- NUEVO CÁLCULO --- */}
        <MiniStat
          label="Pagados"
          value={calcularBoletosPagados(ev).boletosPagados}
          colorClass="text-green-600 dark:text-green-400"
        />
        <MiniStat
          label="% Pagado"
          value={`${calcularBoletosPagados(ev).porcentajePagados}%`}
          colorClass={
            calcularBoletosPagados(ev).porcentajePagados >= 50
              ? "text-green-600 dark:text-green-400"
              : "text-amber-600 dark:text-amber-400"
          }
        />
      </div>

      {/* Detalles expandibles */}
      {expanded && (
        <div className="border-t border-gray-100 dark:border-gray-700 p-4 space-y-2">
          <DetailRow
            label="Monto apartados"
            value={`$${ev.boletosApartadosDinero.toLocaleString()}`}
          />
          <DetailRow
            label="Monto pagados"
            value={`$${ev.boletosPagadosDinero.toLocaleString()}`}
            valueClass="text-green-600 dark:text-green-400"
          />
          <DetailRow
            label="Abono realizado"
            value={`$${ev.abonoRealizado.toLocaleString()}`}
            valueClass="text-blue-600 dark:text-blue-400"
          />
          <DetailRow
            label="Por pagar"
            value={`${ev.boletosPorPagar} · $${ev.boletosPorPagarDinero.toLocaleString()}`}
            valueClass="text-amber-600 dark:text-amber-400"
          />
          <DetailRow
            label="% Abonado"
            value={`${ev.porcentajeAbonado}%`}
            valueClass={ev.porcentajeAbonado >= 20 ? "text-blue-600 dark:text-blue-400" : "text-gray-600"}
          />
        </div>
      )}
    </div>
  );
}

/* ──────────────────────────────────────────────
   Tarjeta de evento reciente (móvil/tablet)
────────────────────────────────────────────── */
function EventoCardReciente({ ev, formatDate, formatDateShort, onVerDetalle }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="bg-white dark:bg-[#1e1e1e] rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden shadow-sm">
      {/* Cabecera */}
      <div className="flex items-start justify-between gap-3 p-4">
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-gray-800 dark:text-gray-100 truncate text-sm">
            {ev.evento?.nombre_evento || "-"}
          </p>
          <p className="text-xs text-gray-500 mt-0.5">
            {formatDateShort(ev.evento?.fecha_evento)}
            {ev.evento?.tipo ? ` · ${ev.evento.tipo}` : ""}
          </p>
          <p className="text-xs text-gray-400 mt-0.5">
            {ev.asistentesAlumnos ? `${ev.asistentesAlumnos} alumnos · ` : ""}
            {ev.asistentes} con boletos · {ev.ocupacion}% ocupación
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={() => onVerDetalle(ev)}
            className="p-2 rounded-lg bg-[#246370] hover:bg-[#1d4f5a] text-white transition-colors shadow-sm"
          >
            <Eye size={15} />
          </button>
          <button
            onClick={() => setExpanded((v) => !v)}
            className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-600 dark:text-gray-300 transition-colors"
            aria-label={expanded ? "Ocultar detalles" : "Ver detalles"}
          >
            {expanded ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
          </button>
        </div>
      </div>

      {/* Resumen siempre visible */}
      <div className="grid grid-cols-3 divide-x divide-gray-100 dark:divide-gray-700 border-t border-gray-100 dark:border-gray-700 px-1">
        <MiniStat label="Apartados" value={ev.boletosApartados} />
        {/* --- CÁLCULO ANTERIOR ---
        <MiniStat
          label="Pagados"
          value={ev.boletosPagados}
          colorClass="text-green-600 dark:text-green-400"
        />
        <MiniStat
          label="% Pagado"
          value={`${ev.porcentajePagados}%`}
          colorClass={ev.porcentajePagados >= 50 ? "text-green-600 dark:text-green-400" : "text-amber-600 dark:text-amber-400"}
        />
        */}
        {/* --- NUEVO CÁLCULO --- */}
        <MiniStat
          label="Pagados"
          value={calcularBoletosPagados(ev).boletosPagados}
          colorClass="text-green-600 dark:text-green-400"
        />
        <MiniStat
          label="% Pagado"
          value={`${calcularBoletosPagados(ev).porcentajePagados}%`}
          colorClass={
            calcularBoletosPagados(ev).porcentajePagados >= 50
              ? "text-green-600 dark:text-green-400"
              : "text-amber-600 dark:text-amber-400"
          }
        />
      </div>

      {/* Detalles expandibles */}
      {expanded && (
        <div className="border-t border-gray-100 dark:border-gray-700 p-4 space-y-2">
          <DetailRow
            label="Monto apartados"
            value={`$${ev.boletosApartadosDinero.toLocaleString()}`}
          />
          <DetailRow
            label="Monto pagados"
            value={`$${ev.boletosPagadosDinero.toLocaleString()}`}
            valueClass="text-green-600 dark:text-green-400"
          />
          <DetailRow
            label="Abono realizado"
            value={`$${ev.abonoRealizado.toLocaleString()}`}
            valueClass="text-blue-600 dark:text-blue-400"
          />
          <DetailRow
            label="Por pagar"
            value={`${ev.boletosPorPagar} · $${ev.boletosPorPagarDinero.toLocaleString()}`}
            valueClass="text-amber-600 dark:text-amber-400"
          />
          <DetailRow
            label="% Abonado"
            value={`${ev.porcentajeAbonado}%`}
            valueClass={ev.porcentajeAbonado >= 20 ? "text-blue-600 dark:text-blue-400" : "text-gray-600"}
          />
        </div>
      )}
    </div>
  );
}

/* ──────────────────────────────────────────────
   Helpers de UI
────────────────────────────────────────────── */
function MiniStat({ label, value, colorClass = "text-gray-800 dark:text-gray-100" }) {
  return (
    <div className="flex flex-col items-center py-2 px-1">
      <span className={`text-base font-semibold ${colorClass}`}>{value}</span>
      <span className="text-[10px] text-gray-400 uppercase tracking-wide mt-0.5 text-center leading-tight">
        {label}
      </span>
    </div>
  );
}

function DetailRow({ label, value, valueClass = "text-gray-700 dark:text-gray-200" }) {
  return (
    <div className="flex items-center justify-between gap-2 text-sm">
      <span className="text-gray-500 dark:text-gray-400 shrink-0">{label}</span>
      <span className={`font-medium text-right ${valueClass}`}>{value}</span>
    </div>
  );
}

function EmptyState({ mensaje }) {
  return (
    <div className="bg-white dark:bg-[#1e1e1e] rounded-xl border border-gray-200 dark:border-gray-700 p-8 text-center text-gray-500 dark:text-gray-400 text-sm">
      {mensaje}
    </div>
  );
}

function Badge({ color, children }) {
  const classes = {
    green: "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300",
    amber: "bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300",
    blue: "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300",
    gray: "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300",
  };
  return (
    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${classes[color] || classes.gray}`}>
      {children}
    </span>
  );
}

function KpiCard({ icon, label, value, subtitle, highlight }) {
  const highlightColors = {
    success: "text-green-600 dark:text-green-400",
    warning: "text-amber-600 dark:text-amber-400",
    danger: "text-red-600 dark:text-red-400",
    info: "text-blue-600 dark:text-blue-400",
  };

  const iconColor = highlight ? highlightColors[highlight] : "text-[#206a73]";

  return (
    <div className="bg-white dark:bg-[#1e1e1e] rounded-xl p-4 border border-gray-200 dark:border-gray-700 flex flex-col gap-1.5 hover:shadow-md transition-shadow h-full">
      <div className={`flex items-center gap-2 ${iconColor}`}>
        {icon}
        <span className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 leading-tight">
          {label}
        </span>
      </div>
      <div className="text-xl sm:text-2xl font-semibold text-gray-800 dark:text-gray-100 leading-tight">
        {value}
      </div>
      {subtitle && (
        <div className="text-xs text-gray-500 dark:text-gray-400 leading-tight">
          {subtitle}
        </div>
      )}
    </div>
  );
}

function Th({ children }) {
  return (
    <th className="px-2 py-2.5 text-left text-xs font-semibold text-white dark:text-gray-400 uppercase tracking-wide whitespace-nowrap">
      {children}
    </th>
  );
}

/** Celda sin wrap (para números y badges) */
function Td({ children, className = "" }) {
  return (
    <td
      className={`px-2 py-2.5 whitespace-nowrap text-gray-700 dark:text-gray-200 text-sm ${className}`}
    >
      {children}
    </td>
  );
}

/** Celda con wrap (para texto largo: nombre, tipo, fecha) */
function TdWrap({ children, className = "" }) {
  return (
    <td
      className={`px-2 py-2.5 text-gray-700 dark:text-gray-200 text-sm leading-snug ${className}`}
    >
      {children}
    </td>
  );
}

/* ──────────────────────────────────────────────
   Skeleton de carga completo
────────────────────────────────────────────── */

/** Bloque con efecto shimmer */
function Sk({ className = "" }) {
  return (
    <div
      className={`relative overflow-hidden rounded-md bg-gray-200 dark:bg-gray-700 ${className}`}
      style={{ isolation: "isolate" }}
    >
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.55) 50%, transparent 100%)",
          animation: "skshimmer 1.4s infinite",
        }}
      />
    </div>
  );
}

function HomeLugarSkeleton() {
  return (
    <>
      {/* Inject shimmer keyframe once */}
      <style>{`
        @keyframes skshimmer {
          0%   { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
      `}</style>

      <div className="min-h-screen bg-fondoVs">
        {/* ── Header skeleton ── */}
        <div className="bg-casalds-700">
          <header className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 sm:p-5 lg:p-6 max-w-7xl mx-auto">
            <div className="flex items-center gap-3">
              {/* Logo placeholder */}
              <Sk className="h-12 w-12 shrink-0 rounded-lg opacity-40" />
              <div className="space-y-2">
                <Sk className="h-5 w-48 opacity-50" />
                <Sk className="h-3 w-32 opacity-40" />
                <Sk className="h-3 w-40 opacity-30" />
              </div>
            </div>
            <div className="flex items-center gap-2 self-end sm:self-auto">
              <Sk className="h-9 w-24 rounded-lg opacity-40" />
              <Sk className="h-9 w-9 rounded-full opacity-40" />
              <Sk className="h-9 w-9 rounded-full opacity-40" />
            </div>
          </header>
        </div>

        {/* ── Contenido ── */}
        <div className="p-4 sm:p-5 lg:p-8 max-w-7xl mx-auto space-y-5 sm:space-y-6 lg:space-y-8">

          {/* KPIs principales (3 cols) */}
          <section className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
            {[...Array(3)].map((_, i) => (
              <div
                key={i}
                className="bg-white dark:bg-[#1e1e1e] rounded-xl p-4 border border-gray-200 dark:border-gray-700 flex flex-col gap-2.5"
              >
                <div className="flex items-center gap-2">
                  <Sk className="h-5 w-5 rounded-md" />
                  <Sk className="h-3 w-24" />
                </div>
                <Sk className="h-8 w-20" />
              </div>
            ))}
          </section>

          {/* KPIs boletos (5 cols) */}
          <section className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
            {[...Array(5)].map((_, i) => (
              <div
                key={i}
                className={`bg-white dark:bg-[#1e1e1e] rounded-xl p-4 border border-gray-200 dark:border-gray-700 flex flex-col gap-2 ${i === 4 ? "col-span-2 sm:col-span-1" : ""}`}
              >
                <div className="flex items-center gap-2">
                  <Sk className="h-4 w-4 rounded-md" />
                  <Sk className="h-3 w-20" />
                </div>
                <Sk className="h-7 w-16" />
                <Sk className="h-3 w-28" />
              </div>
            ))}
          </section>

          {/* Próximos eventos */}
          <section className="space-y-3">
            <Sk className="h-5 w-44" />
            {/* Mobile cards skeleton (visible < lg) */}
            <div className="lg:hidden space-y-3">
              {[...Array(3)].map((_, i) => (
                <div
                  key={i}
                  className="bg-white dark:bg-[#1e1e1e] rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden shadow-sm"
                >
                  <div className="flex items-start justify-between gap-3 p-4">
                    <div className="flex-1 space-y-2">
                      <Sk className="h-4 w-3/4" />
                      <Sk className="h-3 w-1/2" />
                    </div>
                    <div className="flex gap-2">
                      <Sk className="h-8 w-8 rounded-lg" />
                      <Sk className="h-8 w-8 rounded-lg" />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 divide-x divide-gray-100 dark:divide-gray-700 border-t border-gray-100 dark:border-gray-700">
                    {[...Array(3)].map((_, j) => (
                      <div key={j} className="flex flex-col items-center py-3 gap-1.5">
                        <Sk className="h-4 w-8" />
                        <Sk className="h-2.5 w-12" />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            {/* Desktop table skeleton (visible >= lg) */}
            <div className="hidden lg:block bg-white dark:bg-[#1e1e1e] rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
              {/* Fake thead */}
              <div className="bg-casal dark:bg-[#23272e] px-3 py-2.5 grid grid-cols-11 gap-2">
                {[...Array(11)].map((_, j) => (
                  <Sk key={j} className="h-3 opacity-50" style={{ background: "rgba(255,255,255,0.25)" }} />
                ))}
              </div>
              {/* Fake rows */}
              {[...Array(4)].map((_, i) => (
                <div
                  key={i}
                  className="grid grid-cols-11 gap-2 px-3 py-3 border-t border-gray-100 dark:border-gray-800"
                >
                  <Sk className="h-4" />
                  <Sk className="h-4 col-span-2" />
                  <Sk className="h-4" />
                  <Sk className="h-4" />
                  <Sk className="h-4" />
                  <Sk className="h-4" />
                  <Sk className="h-4" />
                  <Sk className="h-5 w-12 rounded-full" />
                  <Sk className="h-5 w-12 rounded-full" />
                  <Sk className="h-7 w-7 rounded-lg justify-self-center" />
                </div>
              ))}
            </div>
          </section>

          {/* Eventos recientes */}
          <section className="space-y-3">
            <Sk className="h-5 w-40" />
            {/* Mobile */}
            <div className="lg:hidden space-y-3">
              {[...Array(3)].map((_, i) => (
                <div
                  key={i}
                  className="bg-white dark:bg-[#1e1e1e] rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden shadow-sm"
                >
                  <div className="flex items-start justify-between gap-3 p-4">
                    <div className="flex-1 space-y-2">
                      <Sk className="h-4 w-2/3" />
                      <Sk className="h-3 w-1/3" />
                      <Sk className="h-3 w-1/2" />
                    </div>
                    <div className="flex gap-2">
                      <Sk className="h-8 w-8 rounded-lg" />
                      <Sk className="h-8 w-8 rounded-lg" />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 divide-x divide-gray-100 dark:divide-gray-700 border-t border-gray-100 dark:border-gray-700">
                    {[...Array(3)].map((_, j) => (
                      <div key={j} className="flex flex-col items-center py-3 gap-1.5">
                        <Sk className="h-4 w-8" />
                        <Sk className="h-2.5 w-12" />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            {/* Desktop table */}
            <div className="hidden lg:block bg-white dark:bg-[#1e1e1e] rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="bg-casal dark:bg-[#23272e] px-3 py-2.5 flex gap-2">
                {[...Array(13)].map((_, j) => (
                  <Sk key={j} className="h-3 flex-1 opacity-50" style={{ background: "rgba(255,255,255,0.25)" }} />
                ))}
              </div>
              {[...Array(5)].map((_, i) => (
                <div
                  key={i}
                  className="flex gap-2 px-3 py-3 border-t border-gray-100 dark:border-gray-800"
                >
                  <Sk className="h-4 flex-1" />
                  <Sk className="h-4" style={{ flexBasis: '14%' }} />
                  <Sk className="h-4 flex-1" />
                  <Sk className="h-4 flex-1" />
                  <Sk className="h-4 flex-1" />
                  <Sk className="h-4 flex-1" />
                  <Sk className="h-4 flex-1" />
                  <Sk className="h-4 flex-1" />
                  <Sk className="h-4 flex-1" />
                  <Sk className="h-5 w-12 rounded-full shrink-0" />
                  <Sk className="h-5 w-12 rounded-full shrink-0" />
                  <Sk className="h-7 w-7 rounded-lg shrink-0" />
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>
    </>
  );
}
